import { useState, useEffect } from 'react';

import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import classes from './ContentTabs.module.scss';

import LectureCard from 'components/common/lecturecard/LectureCard';
import CourseCard from 'components/common/coursecard/CourseCard';
import CategoryEmptyPage from 'components/pages/category/CategoryEmptyPage';
import Spinner from '../spinner/Spinner';

import Options from './options/Options';
import SearchBar from './searchBar/SearchBar';
import Pagination from './pagination/Pagination';

import EmptySearch from 'assets/icons/search-failed.svg?url';
import EmptyState from 'assets/images/empty-state.svg?url';

const ContentTabs = ({
	metadata,
	service,
	tabs,
	search,
	pagination,
	sort,
	filters,
	className,
	source
}) => {
	const router = useRouter();

	const [searchParams, setSearchParams] = useState({});

	const [isUrlLoaded, setIsUrlLoaded] = useState(false);

	useEffect(() => {
		if (router.isReady) {
			setSearchParams({ ...router.query });
		}
	}, [router.isReady]);

	useEffect(() => {
		// Simulate URL loading delay
		setTimeout(() => {
			setIsUrlLoaded(true);
		}, 1000); // Adjust the delay as needed
	}, []);

	const { data, isLoading } = useQuery(
		['content-tabs', ...Object.values(router.query), ...Object.values(searchParams), router.asPath],
		() =>
			service(
				Object.keys(router.query)
					.map((key) => `${key}=${router.query[key]}`)
					.join('&')
			),
		{ enabled: Boolean(isUrlLoaded && router.isReady) }
	);
	const contentTypeHandler = (value) => {
		setSearchParams((prevState) => ({ ...prevState, content: value }));
		router.replace(
			{
				pathname: router.asPath.split('?')[0],
				query: { ...searchParams, content: value }
			},
			undefined,
			{ shallow: true }
		);
	};

	return (
		<div className={`pb-xl-4 py-0 mb-5 mt-xl-4 pb-2 container ${className ?? ''}`}>
			<div className={`${classes['search-header']} col-12`} id="search-bar-header">
				{search && (
					<div className="col-xl-9 col-12">
						<SearchBar />
					</div>
				)}
				{tabs && (
					<div className={`${classes['content-type']} col-12`}>
						<button onClick={() => contentTypeHandler('')} disabled={!searchParams.content}>
							All
						</button>
						<button
							onClick={() => contentTypeHandler('course')}
							disabled={searchParams.content === 'course'}>
							Job Qualifications{' '}
							{metadata?.course_count !== undefined && `(${metadata.course_count})`}
						</button>
						<button
							onClick={() => contentTypeHandler('lecture')}
							disabled={searchParams.content === 'lecture'}>
							Courses {metadata?.lecture_count !== undefined && `(${metadata.lecture_count})`}
						</button>
					</div>
				)}
			</div>

			<div className="d-flex gap-5 justify-content-between">
				<div
					className={`${classes['search-page-result']} ${
						sort || filters ? 'col-xl-8' : 'col-xl-12'
					} col-12 d-flex flex-column`}>
					{search && 'keyword' in searchParams ? (
						<>
							{!searchParams.keyword
								? !isLoading && (
										<div
											className={`${classes['search-page-init']} d-flex flex-column justify-content-center align-items-center`}>
											<h4>Enter your keywords to search for job qualifications or courses</h4>
										</div>
								  )
								: data?.data.results.length === 0 && (
										<div
											className={` ${classes['search-page-empty']} d-flex flex-column justify-content-center align-items-center`}>
											<img src={EmptySearch} alt="" />
											<h4>No Result for “{searchParams.keyword}”</h4>
											<p>Try again with different words</p>
										</div>
								  )}
						</>
					) : (
						data?.data.results.length === 0 &&
						(source !== 'category' ? (
							<div
								className={` ${classes['search-page-empty']} d-flex flex-column justify-content-center align-items-center align-self-center`}>
								<img src={EmptyState} alt="" />
								<h4>Oh hh!</h4>
								<p>There are no contents</p>
							</div>
						) : (
							<CategoryEmptyPage />
						))
					)}
					{
						<>
							{isLoading && isUrlLoaded ? (
								<div
									className={`${classes['search-page-loading']} d-flex flex-row justify-content-center align-items-center`}>
									<Spinner />
								</div>
							) : (
								data?.data.results.map(
									(content) =>
										(content.type === 'course' && (
											<CourseCard key={content.id + content.type} course={content} />
										)) ||
										(content.type === 'lecture' && (
											<LectureCard key={content.id + content.type} lecture={content} />
										))
								)
							)}
						</>
					}
					{pagination && !!data?.data.count && <Pagination dataCount={data.data.count} />}
				</div>
				{(sort || filters) && <Options filters={filters} sort={sort} />}
			</div>
		</div>
	);
};

export default ContentTabs;
